import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shield, FileText, Check, X, InfinityIcon } from "lucide-react";

const OPTIONS = [
  { key: "never_share", label: "Never Share", desc: "Future advocates can never see your prior consultation records." },
  { key: "ask_every_time", label: "Ask Every Time", desc: "You'll be prompted whenever a future advocate requests access. Recommended." },
  { key: "always_share", label: "Always Share", desc: "Any future Advoset advocate can access your prior records without prompting." },
];

/**
 * Client Privacy panel + Pending access requests.
 * Rendered inside the Dashboard's "Records & Privacy" tab.
 */
export default function RecordsPrivacyPanel() {
  const [pref, setPref] = useState({ default: "ask_every_time" });
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([
        api.get("/me/privacy/lcr-sharing"),
        api.get("/me/lcr-access-requests?state=pending"),
      ]);
      setPref(p.data);
      setRequests(r.data.items || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePref = async (next) => {
    setBusy(true);
    try {
      const r = await api.put("/me/privacy/lcr-sharing", { default: next });
      setPref(r.data);
      toast.success("Preference saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const decide = async (id, decision) => {
    setBusy(true);
    try {
      await api.post(`/me/lcr-access-requests/${id}/decide`, { decision });
      toast.success(decision === "denied" ? "Access denied" : decision === "always_allow" ? "Access granted (always)" : "Access granted (once)");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decide");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="records-privacy-panel">
      {/* Pending access requests */}
      {requests.length > 0 && (
        <section className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 grid place-items-center"><Shield size={16}/></div>
            <div>
              <h3 className="text-sm font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Advocate access requests</h3>
              <p className="text-xs text-slate-500">These advocates would like to view a past consultation record.</p>
            </div>
          </div>
          <ul className="space-y-2" data-testid="access-requests-list">
            {requests.map((r) => (
              <li key={r.id} className="border border-slate-200 rounded-2xl p-3">
                <div className="flex items-start gap-2">
                  <FileText size={16} className="text-[#0A2540] mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0A2540]">{r.requesting_lawyer_name} requested access</p>
                    <p className="text-xs text-slate-500">
                      Record: <b>{r.matter_title}</b> ({r.date})
                    </p>
                    {r.reason && <p className="mt-1 text-xs text-slate-500 italic">&quot;{r.reason}&quot;</p>}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => decide(r.id, "allow_once")} disabled={busy} data-testid={`allow-once-${r.id}`}
                          className="px-3 py-1.5 text-[11px] font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
                    <Check size={12} className="inline"/> Allow Once
                  </button>
                  <button onClick={() => decide(r.id, "always_allow")} disabled={busy} data-testid={`always-allow-${r.id}`}
                          className="px-3 py-1.5 text-[11px] font-bold rounded-full bg-[#0A2540] text-[#D4AF37] hover:opacity-90 inline-flex items-center gap-1">
                    <InfinityIcon size={12}/> Always Allow
                  </button>
                  <button onClick={() => decide(r.id, "denied")} disabled={busy} data-testid={`deny-${r.id}`}
                          className="px-3 py-1.5 text-[11px] font-bold rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100">
                    <X size={12} className="inline"/> Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Default preference */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-9 w-9 rounded-xl bg-[#FBF4DF] text-[#8a6d14] grid place-items-center"><Shield size={16}/></div>
          <div>
            <h3 className="text-sm font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Default sharing behaviour</h3>
            <p className="text-xs text-slate-500">Controls how future advocates can access your prior consultation records.</p>
          </div>
        </div>
        <div className="mt-4 grid gap-2">
          {OPTIONS.map((o) => (
            <label key={o.key} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
              pref.default === o.key ? "border-[#0A2540] bg-[#FBF4DF]/40" : "border-slate-200 hover:border-slate-300"
            }`} data-testid={`privacy-option-${o.key}`}>
              <input
                type="radio"
                name="lcr-privacy"
                className="mt-1"
                checked={pref.default === o.key}
                onChange={() => savePref(o.key)}
                disabled={busy}
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#0A2540]">{o.label}</p>
                <p className="text-xs text-slate-500">{o.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}
